
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Table, Button, Space, message, Modal, Tabs, Tag, Switch, Typography, Input, Checkbox, Row, Col, Tooltip, Select, notification, Form, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SyncOutlined, DollarOutlined, GoldOutlined, ThunderboltOutlined, EyeOutlined, StarOutlined } from '@ant-design/icons';
import { deleteProduct, getAutoSyncStatus, setAutoSyncStatus, triggerManualSync, getDopingQuota, dopeProduct, undopeProduct, DopingQuota, Product } from '../api/product';
import { getGemstones, getProductStones, addProductStone, deleteProductStone, getStoneLists, Gemstone } from '../api/gemstones';
import { bulkAITranslate, bulkLuxuryDescribe, cleanupDescriptions, getAITasks } from '../api/ai';
import client from '../api/client';
import AddProduct from './AddProduct';
import AITaskProgress from './AITaskProgress';

const { Text } = Typography;

const ProductList: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [autoSync, setAutoSync] = useState(true);
    const [goldPrice, setGoldPrice] = useState<any>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMarketplaces, setSelectedMarketplaces] = useState<string[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [filterCategoryId, setFilterCategoryId] = useState<string | undefined>(undefined);
    const [filterMinPrice, setFilterMinPrice] = useState('');
    const [filterMaxPrice, setFilterMaxPrice] = useState('');
    // Toplu lüks açıklama modalı: dil modu 'en' = sadece İngilizce,
    // 'all' = 5 dilde açıklama + otomatik etiket.
    const [luxuryModalOpen, setLuxuryModalOpen] = useState(false);
    const [luxuryLangMode, setLuxuryLangMode] = useState<'en' | 'all'>('all');
    const [luxuryLoading, setLuxuryLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 15 });
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [translateLoading, setTranslateLoading] = useState(false);
    const [aiProgressVisible, setAiProgressVisible] = useState(false);
    // AI izleme modu: toplu işlem sürerken buton "İzle" olur, bitince
    // bildirim gelir ve buton tekrar "Çevir" olur.
    const [aiWatching, setAiWatching] = useState(false);
    const [aiActiveCount, setAiActiveCount] = useState(0);
    const aiDoneRef = useRef(false);
    // Son toplu işlemin kuyruğa atıldığı an — bitiş bildirimi SADECE bu
    // partinin sonuçlarını sayar (geçmişteki binlerce iş karıştırılmaz).
    const aiBatchStartRef = useRef<number>(0);
    const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
    const [cleanupKeyword, setCleanupKeyword] = useState('');
    const [cleanupAction, setCleanupAction] = useState<'clear_matching' | 'clear_all'>('clear_matching');
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [stonesProduct, setStonesProduct] = useState<Product | null>(null);
    const [stonesList, setStonesList] = useState<any[]>([]);
    const [gemCatalog, setGemCatalog] = useState<Gemstone[]>([]);
    const [stoneCuts, setStoneCuts] = useState<string[]>([]);
    const [stoneQualities, setStoneQualities] = useState<string[]>([]);
    const [stonesLoading, setStonesLoading] = useState(false);
    const [stoneForm] = Form.useForm();

    const openStones = async (record: Product) => {
        setStonesProduct(record);
        stoneForm.resetFields();
        setStonesLoading(true);
        try {
            const [stones, gems, lists] = await Promise.all([
                getProductStones(record.id).catch(() => []),
                getGemstones().catch(() => []),
                getStoneLists().catch(() => null)
            ]);
            setStonesList(Array.isArray(stones) ? stones : []);
            setGemCatalog(Array.isArray(gems) ? gems : []);
            if (lists) {
                setStoneCuts(lists.cuts);
                setStoneQualities(lists.qualities);
            }
        } finally {
            setStonesLoading(false);
        }
    };

    const handleAddStone = async (values: any) => {
        if (!stonesProduct) return;
        try {
            await addProductStone(stonesProduct.id, values);
            message.success('Taş eklendi');
            stoneForm.resetFields();
            setStonesList(await getProductStones(stonesProduct.id).catch(() => []));
        } catch (err: any) {
            message.error(err?.response?.data?.error || 'Taş eklenemedi');
        }
    };

    const handleDeleteStone = async (stoneId: string) => {
        if (!stonesProduct) return;
        try {
            await deleteProductStone(stoneId);
            message.success('Taş kaldırıldı');
            setStonesList(await getProductStones(stonesProduct.id).catch(() => []));
        } catch {
            message.error('İşlem başarısız');
        }
    };
    const [dopingQuota, setDopingQuota] = useState<DopingQuota | null>(null);

    const isDopedActive = (p: Product) =>
        !!p.isDoped && (!p.dopedUntil || new Date(p.dopedUntil).getTime() > Date.now());

    const fetchDopingQuota = async () => {
        try {
            setDopingQuota(await getDopingQuota());
        } catch {
            setDopingQuota(null);
        }
    };

    const handleDope = async (record: Product) => {
        try {
            await dopeProduct(record.id);
            message.success(`Doping aktif (${dopingQuota?.durationDays || 30} gün) — ürün vitrinde öne çıkar`);
            refreshProducts();
            fetchDopingQuota();
        } catch (err: any) {
            message.error(err?.response?.data?.error?.message || err?.response?.data?.error || 'Doping başarısız');
        }
    };

    const handleUndope = async (record: Product) => {
        try {
            await undopeProduct(record.id);
            message.success('Doping kaldırıldı');
            refreshProducts();
            fetchDopingQuota();
        } catch {
            message.error('İşlem başarısız');
        }
    };

    useEffect(() => {
        setPagination(p => ({ ...p, current: 1 }));
        fetchProducts(searchTerm, selectedMarketplaces, filterCategoryId, filterMinPrice, filterMaxPrice);
        fetchGoldPrice();
        fetchSyncStatus();
        fetchCategories();
    }, [searchTerm, selectedMarketplaces, filterCategoryId, filterMinPrice, filterMaxPrice]);

    const fetchCategories = async () => {
        try {
            const { data } = await client.get('/categories');
            const list = Array.isArray(data) ? data : (data?.data || []);
            setCategories(list);
        } catch {
            /* sessiz geç */
        }
    };

    const fetchGoldPrice = async () => {
        try {
            const res = await client.get('/gold-price/current');
            setGoldPrice(res.data);
        } catch (error) {
            console.error('Failed to fetch gold price', error);
        }
    };

    const fetchSyncStatus = async () => {
        try {
            const status = await getAutoSyncStatus();
            setAutoSync(!!status);
        } catch (error) {
            console.error('Failed to fetch sync status', error);
        }
    };

    const handleSyncStatusChange = async (checked: boolean) => {
        try {
            await setAutoSyncStatus(checked);
            setAutoSync(checked);
            message.success(checked ? 'Otomatik senkronizasyon açıldı.' : 'Otomatik senkronizasyon kapatıldı.');
        } catch (error) {
            message.error('Ayar güncellenemedi.');
        }
    };

    const handleManualSync = async () => {
        setSyncing(true);
        try {
            const res = await triggerManualSync();
            message.success(res.message || 'Fiyatlar başarıyla senkronize edildi.');
            refreshProducts();
        } catch (error) {
            message.error('Senkronizasyon sırasında hata oluştu.');
        } finally {
            setSyncing(false);
        }
    };

    const fetchProducts = async (search?: string, marketplaces?: string[], categoryId?: string, minPrice?: string, maxPrice?: string) => {
        setLoading(true);
        try {
            const marketplaceParams = marketplaces && marketplaces.length > 0 ? marketplaces : undefined;
            // Satıcının tüm ürünleri tek seferde çekilir (tab sayaçları ve seçim
            // bu listeden türetilir). Limit 100 iken 100+ üründe hem liste eksik
            // kalıyor hem sayaçlar tutmuyordu.
            const response = await client.get('/products', {
                params: {
                    limit: 1000,
                    search,
                    marketplaces: marketplaceParams?.join(','),
                    categoryId: categoryId || undefined,
                    minPrice: minPrice || undefined,
                    maxPrice: maxPrice || undefined,
                }
            });
            const data = response.data?.data || [];
            const pagination = response.data?.pagination;
            setProducts(data);
            if (pagination?.total !== undefined) {
                setTotalCount(pagination.total);
            }
        } catch (error) {
            console.error(error);
            message.error('Ürünler yüklenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, title: string) => {
        Modal.confirm({
            title: 'Ürünü Sil',
            content: (
                <div>
                    <p>"{title}" ürününü silmek istediğinize emin misiniz?</p>
                    <p style={{ color: '#ff4d4f', fontWeight: 600 }}>Bu işlem geri alınamaz.</p>
                </div>
            ),
            okText: 'Evet, Sil',
            okType: 'danger',
            cancelText: 'İptal',
            onOk: async () => {
                try {
                    await deleteProduct(id);
                    message.success('Ürün silindi.');
                    refreshProducts();
                } catch (error) {
                    message.error('Silme işlemi başarısız.');
                }
            }
        });
    };

    const handleEdit = (product: Product) => {
        setEditingProduct(product);
        setIsModalVisible(true);
    };

    const handleAdd = () => {
        setEditingProduct(null);
        setIsModalVisible(true);
    };

    const handleModalClose = (refresh: boolean) => {
        setIsModalVisible(false);
        if (refresh) refreshProducts();
    };

    const handleAIDone = useCallback(async () => {
        if (aiDoneRef.current) return;
        aiDoneRef.current = true;
        setAiWatching(false);
        setAiActiveCount(0);
        setAiProgressVisible(false);
        // Partinin gerçek sonucu: kaç başarılı / kaç başarısız + ilk hata.
        // (Eskiden her durumda "tamamlandı" deniyordu.)
        try {
            const all = await getAITasks(undefined, 200);
            const arr = Array.isArray(all) ? all : [];
            const batch = arr.filter((t: any) => {
                const ts = new Date(t.createdAt).getTime();
                return !isNaN(ts) && ts >= aiBatchStartRef.current;
            });
            if (aiBatchStartRef.current > 0 && batch.length > 0) {
                const ok = batch.filter((t: any) => t.status === 'completed').length;
                const fail = batch.filter((t: any) => t.status === 'failed').length;
                const firstErr = (batch.find((t: any) => t.status === 'failed') as any)?.error;
                const desc = `${batch.length} üründen ${ok} başarılı${fail > 0 ? `, ${fail} başarısız` : ''}. Liste güncellendi.` +
                    (firstErr ? ` İlk hata: ${firstErr}` : '');
                if (fail > 0 && ok === 0) {
                    notification.error({ message: 'AI işlemleri başarısız', description: desc, duration: 10 });
                } else if (fail > 0) {
                    notification.warning({ message: 'AI işlemleri kısmen tamamlandı', description: desc, duration: 10 });
                } else {
                    notification.success({ message: 'AI işlemleri tamamlandı', description: desc, duration: 6 });
                }
                refreshProducts();
                return;
            }
        } catch {
            /* özet alınamazsa genel mesaja düş */
        }
        notification.success({
            message: 'AI işlemleri tamamlandı',
            description: 'Seçili ürünlerin çevirileri işlendi. Liste güncellendi.',
            duration: 6
        });
        refreshProducts();
    }, []);

    const handleQueueCleared = useCallback(() => {
        aiDoneRef.current = true;
        setAiWatching(false);
        setAiActiveCount(0);
        setAiProgressVisible(false);
        notification.info({
            message: 'Kuyruk temizlendi',
            description: 'Bekleyen işlemler iptal edildi. Çevirisi biten ürünler duruyor; kalanları yeniden seçip çevirebilirsin.',
            duration: 6
        });
        refreshProducts();
    }, []);

    // İzleme modu açıkken kuyruğu yokla: bitince bildir + butonu geri çevir.
    // (İlerleme penceresi kapalı olsa bile çalışır.)
    useEffect(() => {
        if (!aiWatching) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const all = await getAITasks(undefined, 200);
                const arr = Array.isArray(all) ? all : [];
                const active = arr.filter((t: any) => t.status === 'pending' || t.status === 'processing');
                if (cancelled) return;
                setAiActiveCount(active.length);
                if (arr.length > 0 && active.length === 0) handleAIDone();
            } catch {
                /* ağ hatasında sessizce tekrar dene */
            }
        };
        poll();
        const id = setInterval(poll, 4000);
        return () => { cancelled = true; clearInterval(id); };
    }, [aiWatching, handleAIDone]);

    // Sayfa yenilense bile sürmekte olan iş varsa izleme moduna gir —
    // buton "AI İzle" olur, "Çevir" aktif kalıp kafa karıştırmaz.
    useEffect(() => {
        fetchDopingQuota();
        (async () => {
            try {
                const all = await getAITasks(undefined, 200);
                const arr = Array.isArray(all) ? all : [];
                const active = arr.filter((t: any) => t.status === 'pending' || t.status === 'processing');
                if (active.length > 0) {
                    aiDoneRef.current = false;
                    setAiActiveCount(active.length);
                    setAiWatching(true);
                }
            } catch {
                /* sessiz geç */
            }
        })();
    }, []);

    const refreshProducts = () => fetchProducts(searchTerm, selectedMarketplaces, filterCategoryId, filterMinPrice, filterMaxPrice);

    const handleBulkTranslate = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Lütfen çevrilecek ürünleri seçin.');
            return;
        }
        setTranslateLoading(true);
        try {
            const ids = selectedRowKeys.map(k => String(k));
            // 'both': açıklaması eksik/kısa ürünlerde önce açıklama üretilir,
            // sonra başlık+a açıklama 5 dile çevrilir. Sadece 'translate'
            // gönderilince boş açıklamalar boş kalıyordu.
            const res = await bulkAITranslate(ids, 'both');
            message.success(res.message || `${ids.length} ürün AI kuyruğuna alındı`);
            aiDoneRef.current = false;
            aiBatchStartRef.current = Date.now();
            setAiWatching(true);
            setAiActiveCount(ids.length);
            setAiProgressVisible(true);
            setSelectedRowKeys([]);
        } catch (err: any) {
            message.error(err?.response?.data?.error || 'Toplu çeviri başlatılamadı');
        } finally {
            setTranslateLoading(false);
        }
    };

    const handleBulkLuxuryDescribe = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Lütfen açıklama yazılacak ürünleri seçin.');
            return;
        }
        setLuxuryLoading(true);
        try {
            const ids = selectedRowKeys.map(k => String(k));
            // luxury_description: mevcut açıklama KULLANILMAZ, sadece başlıktan
            // lüks/SEO açıklaması üretilir + otomatik etiket eklenir.
            const res = await bulkLuxuryDescribe(ids, luxuryLangMode);
            message.success(res.message || `${ids.length} ürün AI kuyruğuna alındı`);
            aiDoneRef.current = false;
            aiBatchStartRef.current = Date.now();
            setAiWatching(true);
            setAiActiveCount(ids.length);
            setAiProgressVisible(true);
            setLuxuryModalOpen(false);
            setSelectedRowKeys([]);
        } catch (err: any) {
            message.error(err?.response?.data?.error || 'Toplu açıklama başlatılamadı');
        } finally {
            setLuxuryLoading(false);
        }
    };

    const handleCleanup = async () => {
        if (cleanupAction === 'clear_matching' && !cleanupKeyword.trim()) {
            message.warning('Lütfen temizlenecek kelimeyi girin.');
            return;
        }
        setCleanupLoading(true);
        try {
            const res = await cleanupDescriptions(cleanupKeyword.trim(), cleanupAction);
            message.success(res.message || `${res.cleaned} ürün temizlendi`);
            setCleanupModalOpen(false);
            setCleanupKeyword('');
            refreshProducts();
        } catch (err: any) {
            message.error(err?.response?.data?.error || 'Temizleme başarısız');
        } finally {
            setCleanupLoading(false);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newKeys: React.Key[]) => setSelectedRowKeys(newKeys),
    };

    const columns = [
        { title: 'Resim', dataIndex: 'images', key: 'images', render: (imgs: string[]) => imgs && imgs.length > 0 ? <img src={imgs[0]} alt="product" style={{ width: 50 }} /> : 'Yok' },
        { 
            title: 'Ürün Adı', 
            dataIndex: 'title', 
            key: 'title',
            render: (text: string, record: Product) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{text}</span>
                    {record.originalStoreName && (
                        <span style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            background: '#fff2e8',
                            color: '#d4380d',
                            border: '1px solid #ffbb96',
                            borderRadius: 4,
                            marginTop: 4,
                            display: 'inline-block',
                            width: 'fit-content'
                        }}>TEDARİK: {record.originalStoreName}</span>
                    )}
                </div>
            )
        },
        { title: 'Kategori', dataIndex: 'category', key: 'category' },
        { title: 'Gram', dataIndex: 'gramWeight', key: 'gramWeight', render: (val: number | null) => val ? `${val} gr` : <span style={{ color: '#bbb' }}>—</span> },
        { 
            title: 'Milyem', 
            key: 'milyem',
            render: (_: any, record: Product) => (
                <div style={{ lineHeight: 1.4 }}>
                    <div><span style={{ color: '#666', fontSize: 11 }}>Alaşım: </span><strong>{record.milyem || '—'}</strong></div>
                    {record.effectiveMilyem && record.effectiveMilyem !== record.milyem && (
                        <div><span style={{ color: '#d4a017', fontSize: 11 }}>Efektif: </span><strong style={{ color: '#d4a017' }}>{record.effectiveMilyem}</strong></div>
                    )}
                </div>
            )
        },
        { 
            title: 'Gram Has', 
            dataIndex: 'gramHas', 
            key: 'gramHas',
            render: (val: number) => val ? (
                <span style={{ color: '#d4a017', fontWeight: 600 }}>
                    {Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} gr has
                </span>
            ) : '—'
        },
        { title: 'Fiyat (TL)', dataIndex: 'priceTRY', key: 'priceTRY', render: (val: number) => `${Number(val).toLocaleString('tr-TR')} ₺` },
        { title: 'Miktar', dataIndex: 'quantity', key: 'quantity' },
        {
            title: 'Doping',
            key: 'doping',
            render: (_: any, record: Product) => isDopedActive(record)
                ? <Tag color="gold">Dopingli</Tag>
                : <Tag color="default">Yok</Tag>
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_: any, record: Product) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Tooltip title="Taşlar (müşteri seçimi + fiyat)">
                        <Button icon={<StarOutlined />} onClick={() => openStones(record)} />
                    </Tooltip>
                    {isDopedActive(record) ? (
                        <Tooltip title="Dopingi kaldır (kota yeri boşalır)">
                            <Button icon={<ThunderboltOutlined />} style={{ color: '#d4a017', borderColor: '#d4a017' }} onClick={() => handleUndope(record)} />
                        </Tooltip>
                    ) : (
                        <Tooltip title={dopingQuota ? `Doping yap (${dopingQuota.used}/${dopingQuota.limit} kullanılıyor)` : 'Doping yap'}>
                            <Button icon={<ThunderboltOutlined />} onClick={() => handleDope(record)} />
                        </Tooltip>
                    )}
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id, record.title)} />
                </Space>
            )
        }
    ];

    // Original classification remains for tabs
    const myProducts = products.filter(p => !p.originalProductId && !p.originalStoreName);
    const b2bProducts = products.filter(p => p.originalProductId || p.originalStoreName);

    // Kontrollü sayfalama: statik { pageSize: 15 } prop'u AntD'nin dahili
    // sayfa boyutu değişimini eziyor, dropdown hep 15 gösteriyordu.
    const tableProps = (data: Product[]) => ({
        dataSource: data,
        columns: columns,
        rowKey: "id",
        loading: loading,
        pagination: {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50', '100'],
            showTotal: (total: number, range: [number, number]) =>
                `${total} üründen ${range[0]}-${range[1]} arası • ${selectedRowKeys.length} seçili`,
            onChange: (page: number, size: number) => setPagination({ current: page, pageSize: size })
        },
        rowSelection
    });

    const tabItems = [
        {
            key: 'my-products',
            // totalCount sunucudaki TÜM ürün sayısındır (B2B kopyalar dahil).
            // Sekme sayacı tabloda görünen filtrelenmiş listedir.
            label: `Kendi Ürünlerim (${myProducts.length})`,
            children: <Table {...tableProps(myProducts)} />
        },
        {
            key: 'b2b-products',
            label: (
                <span>
                    Tedarik Edilenler (B2B)
                    <Tag color="orange" style={{ marginLeft: 8, borderRadius: 10 }}>{b2bProducts.length}</Tag>
                </span>
            ),
            children: <Table {...tableProps(b2bProducts)} />
        }
    ];

    return (
        <div>
             {/* Header Area with Gold Rates, Sync Settings, Search and Filters */}
              <Row gutter={[16, 16]} style={{ marginBottom: 20 }} align="middle">
                  <Col span={8}>
                      <h2 style={{ marginBottom: 0 }}>Tüm Ürünler</h2>
                      <Text type="secondary">Toplam {totalCount} ürün{products.length !== totalCount ? ` (${products.length} listelendi)` : ''}</Text>
                  </Col>
                  <Col span={16} style={{ textAlign: 'right' }}>
                      <Space size="large" align="center" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'flex-end' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '280px' }}>
                              {/* Search Input */}
                              <Input.Search
                                placeholder="Ürün ara..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onPressEnter={() => refreshProducts()}
                                style={{ width: 220 }}
                              />

                              {/* Category + Price Filters */}
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <Select
                                  placeholder="Kategori"
                                  allowClear
                                  value={filterCategoryId}
                                  onChange={v => setFilterCategoryId(v)}
                                  style={{ width: 180 }}
                                  options={categories.map((c: any) => ({ label: c.name || c.slug, value: c.id }))}
                                />
                                <Input
                                  placeholder="Min ₺"
                                  value={filterMinPrice}
                                  onChange={e => setFilterMinPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                                  style={{ width: 100 }}
                                />
                                <Input
                                  placeholder="Max ₺"
                                  value={filterMaxPrice}
                                  onChange={e => setFilterMaxPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                                  style={{ width: 100 }}
                                />
                                {(filterCategoryId || filterMinPrice || filterMaxPrice) && (
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() => { setFilterCategoryId(undefined); setFilterMinPrice(''); setFilterMaxPrice(''); }}
                                  >
                                    Temizle
                                  </Button>
                                )}
                              </div>
                              
                              {/* Marketplace Filters */}
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <Checkbox.Group
                                  options={[
                                    { label: 'Golden Marketplace', value: 'golden' },
                                    { label: 'Etsy', value: 'etsy' },
                                    { label: 'Trendyol', value: 'trendyol' },
                                    { label: 'Amazon', value: 'amazon' },
                                    { label: 'N11', value: 'n11' },
                                    { label: 'Hepsiburada', value: 'hepsiburada' },
                                    { label: 'Pazarama', value: 'pazarama' }
                                  ]}
                                  value={selectedMarketplaces}
                                  onChange={values => {
                                    setSelectedMarketplaces(values);
                                    fetchProducts(searchTerm, values, filterCategoryId, filterMinPrice, filterMaxPrice);
                                  }}
                                />
                              </div>
                          </div>
                         
                         {goldPrice && (
                             <Space size="middle">
                                 <Tag color="gold" style={{ padding: '4px 10px', fontSize: 13, border: '1px solid #d4a017', background: '#fffbe6' }}>
                                     <GoldOutlined style={{ marginRight: 6 }} />
                                     24K Gram Has: <strong>{Number(goldPrice.pricePerGramTRY).toLocaleString('tr-TR')} ₺</strong>
                                 </Tag>
                                 <Tag color="blue" style={{ padding: '4px 10px', fontSize: 13, border: '1px solid #91d5ff', background: '#e6f7ff' }}>
                                     <DollarOutlined style={{ marginRight: 6 }} />
                                     USD/TRY: <strong>{Number(goldPrice.usdTryRate).toLocaleString('tr-TR')} ₺</strong>
                                 </Tag>
                             </Space>
                         )}
                         
                         <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
                             <Text style={{ marginRight: 8, fontSize: 12 }}>Oto-Fiyat Senkronu:</Text>
                             <Switch 
                                 checked={autoSync} 
                                 onChange={handleSyncStatusChange} 
                                 checkedChildren="Açık" 
                                 unCheckedChildren="Kapalı"
                                 size="small"
                                 style={{ background: autoSync ? '#52c41a' : undefined }}
                             />
                         </div>

                          <Button type="default" icon={<SyncOutlined spin={syncing} />} onClick={handleManualSync} loading={syncing}>
                              Fiyatları Senkronize Et
                          </Button>
                           {aiWatching ? (
                                <Tooltip title="AI çevirisi sürüyor — tıklayıp ilerlemeyi izleyin">
                                    <Button
                                        type="default"
                                        icon={<EyeOutlined />}
                                        onClick={() => setAiProgressVisible(true)}
                                        style={{ borderColor: '#722ed1', color: '#722ed1' }}
                                    >
                                        AI İzle ({aiActiveCount})
                                    </Button>
                                </Tooltip>
                            ) : (
                            <Tooltip title={selectedRowKeys.length === 0 ? 'Önce ürünleri seçin' : 'Seçili ürünlerin BAŞLIK ve AÇIKLAMALARI 5 dile çevrilir (model kodları aynen korunur)'}>
                                <Button
                                    type="default"
                                    icon={<ThunderboltOutlined />}
                                    onClick={handleBulkTranslate}
                                    loading={translateLoading}
                                    disabled={selectedRowKeys.length === 0}
                                    style={{ borderColor: '#722ed1', color: '#722ed1' }}
                                >
                                    AI ile Çevir ({selectedRowKeys.length})
                                </Button>
                            </Tooltip>
                            )}
                            <Tooltip title={selectedRowKeys.length === 0 ? 'Önce ürünleri seçin' : 'Seçili ürünlere SADECE başlıktan lüks/SEO açıklaması yazılır (mevcut açıklama kullanılmaz) + otomatik etiket'}>
                                <Button
                                    type="default"
                                    icon={<EditOutlined />}
                                    onClick={() => setLuxuryModalOpen(true)}
                                    disabled={selectedRowKeys.length === 0 || aiWatching}
                                    style={{ borderColor: '#d4a017', color: '#d4a017' }}
                                >
                                    AI ile Açıklama Yaz ({selectedRowKeys.length})
                                </Button>
                            </Tooltip>
                           <Button
                               type="default"
                               onClick={() => setCleanupModalOpen(true)}
                               style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                           >
                               Açıklamaları Temizle
                           </Button>
                           <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                               Yeni Ürün Ekle
                           </Button>
                      </Space>
                 </Col>
             </Row>

            <Tabs defaultActiveKey="my-products" items={tabItems} />

            <Modal
                title={editingProduct ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
                open={isModalVisible}
                onCancel={() => handleModalClose(false)}
                footer={null}
                destroyOnClose
                width={1000}
                style={{ maxWidth: '95vw', top: 20 }}
                styles={{ body: { maxHeight: '82vh', overflowY: 'auto' } }}
            >
                <AddProduct
                    initialValues={editingProduct}
                    onSuccess={() => handleModalClose(true)}
                />
            </Modal>

            <Modal
                title="Açıklamaları Temizle"
                open={cleanupModalOpen}
                onCancel={() => { setCleanupModalOpen(false); setCleanupKeyword(''); }}
                onOk={handleCleanup}
                confirmLoading={cleanupLoading}
                okText="Temizle"
                okButtonProps={{ danger: true }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>Bu işlem ürün açıklamalarını kalıcı olarak temizler.</Text>
                </div>
                <div style={{ marginBottom: 12 }}>
                    <Text strong>Eylem</Text>
                    <Select
                        value={cleanupAction}
                        onChange={v => setCleanupAction(v)}
                        style={{ width: '100%', marginTop: 4 }}
                        options={[
                            { label: 'Kelime/ifade içerenleri temizle', value: 'clear_matching' },
                            { label: 'Tüm ürünlerin açıklamalarını temizle', value: 'clear_all' }
                        ]}
                    />
                </div>
                {cleanupAction === 'clear_matching' && (
                    <div>
                        <Text strong>Temizlenecek kelime veya cümle</Text>
                        <Input
                            value={cleanupKeyword}
                            onChange={e => setCleanupKeyword(e.target.value)}
                            placeholder="Örn: &amp;amp;lt;, gratis, ürün açıklaması"
                            style={{ marginTop: 4 }}
                        />
                    </div>
                )}
            </Modal>

            <Modal
                title="AI ile Toplu Açıklama Yaz"
                open={luxuryModalOpen}
                onCancel={() => setLuxuryModalOpen(false)}
                onOk={handleBulkLuxuryDescribe}
                confirmLoading={luxuryLoading}
                okText={`${selectedRowKeys.length} ürüne açıklama yaz`}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>
                        Seçili <Text strong>{selectedRowKeys.length} ürün</Text> için mevcut açıklama{' '}
                        <Text strong>kullanılmadan</Text>, <Text strong>sadece ürün başlığından</Text> lüks,
                        SEO uyumlu profesyonel açıklama üretilir. Başlıktaki karat (22K, 14K, 585...), gram ve
                        ölçü bilgileri özellik listesi olarak alt alta yazılır. Açıklamaya göre otomatik etiketler de oluşturulur.
                    </Text>
                </div>
                <div>
                    <Text strong>Açıklama dili</Text>
                    <Select
                        value={luxuryLangMode}
                        onChange={v => setLuxuryLangMode(v)}
                        style={{ width: '100%', marginTop: 4 }}
                        options={[
                            { label: '5 dilde (EN + TR + IT + ES + AR) + etiket', value: 'all' },
                            { label: 'Sadece İngilizce + etiket', value: 'en' }
                        ]}
                    />
                </div>
            </Modal>

            <Modal
                title={`Taşlar: ${stonesProduct?.title || ''} (${stonesList.length}/5 çeşit)`}
                open={!!stonesProduct}
                onCancel={() => setStonesProduct(null)}
                footer={null}
                destroyOnClose
                width={640}
            >
                {stonesProduct && (
                    <p style={{ color: '#666', marginBottom: 12 }}>
                        Taban fiyat: <strong>{Number(stonesProduct.priceTRY || 0).toLocaleString('tr-TR')} ₺</strong>
                        <span style={{ color: '#999' }}> (taş seçimi sepette üstüne eklenir)</span>
                    </p>
                )}
                {gemCatalog.length === 0 && (
                    <p style={{ color: '#888' }}>
                        Önce <a href="/gemstones">Taşlar</a> sayfasından katalog oluşturun.
                    </p>
                )}
                <Table
                    dataSource={stonesList}
                    rowKey="id"
                    loading={stonesLoading}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 16 }}
                    columns={[
                        { title: 'Taş', key: 'gem', render: (_: any, r: any) => r.gemstoneName || r.gemstone?.name || '—' },
                        { title: 'Karat', dataIndex: 'carat', key: 'carat', render: (v: any) => v ?? '—' },
                        { title: 'Kesim', dataIndex: 'cut', key: 'cut', render: (v: any) => v || '—' },
                        { title: 'Özellik', dataIndex: 'quality', key: 'quality', render: (v: any) => v || '—' },
                        { title: 'Fiyat', dataIndex: 'priceTRY', key: 'priceTRY', render: (v: any) => v > 0 ? `${Number(v).toLocaleString('tr-TR')} ₺` : 'Teşhir' },
                        { title: 'Tür', key: 'fixed', render: (_: any, r: any) => r.isFixed ? <Tag>Sabit</Tag> : <Tag color="blue">Seçimli</Tag> },
                        {
                            title: '', key: 'x', width: 50,
                            render: (_: any, r: any) => (
                                <Button size="small" danger onClick={() => handleDeleteStone(r.id)}>Kaldır</Button>
                            )
                        }
                    ]}
                />
                {stonesList.length >= 5 ? (
                    <p style={{ color: '#888' }}>En fazla 5 çeşit taş eklenebilir. Yeni eklemek için birini kaldırın.</p>
                ) : (
                <Form form={stoneForm} layout="vertical" onFinish={handleAddStone}>
                    <Row gutter={8}>
                        <Col span={6}>
                            <Form.Item name="gemstoneId" label="Taş" rules={[{ required: true }]}>
                                <Select placeholder="Katalogdan seç">
                                    {gemCatalog.filter(g => g.isActive).map(g => (
                                        <Select.Option key={g.id} value={g.id}>{g.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item name="carat" label="Karat">
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="cut" label="Kesim">
                                <Select placeholder="Seç" allowClear>
                                    {stoneCuts.map((c: string) => (
                                        <Select.Option key={c} value={c}>{c}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="quality" label="Özellik">
                                <Select placeholder="Seç" allowClear>
                                    {stoneQualities.map((q: string) => (
                                        <Select.Option key={q} value={q}>{q}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="isFixed" label="Sabit taş" valuePropName="checked" tooltip="Seçim yapılamaz, bilgi olarak gösterilir">
                                <Switch size="small" />
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item label=" " >
                                <Button type="primary" htmlType="submit">Ekle</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                )}
            </Modal>

            <AITaskProgress
                visible={aiProgressVisible}
                onClose={() => setAiProgressVisible(false)}
                onAllComplete={handleAIDone}
                onQueueCleared={handleQueueCleared}
            />
        </div>
    );
};

export default ProductList;
